import { sseUrl } from '@/services/reqconfig/aiwriter'
import { ELKReportName } from '@/types'
import { AiStatus, AiWriterOption, AiWriterProps, CallBack } from '@/types/aiAsk'
import { elkReport } from '@/utils'
import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source'
import { useRef, useState } from 'react'
import { flushSync } from 'react-dom'

export const useAiWriter = (props?: AiWriterProps) => {
  const { delayTime = 50, askDelayMsgCb, dependOnUser = false } = props || {}
  const abort = useRef<AbortController | null>(null) // useRef记忆值
  const [content, setContent] = useState('')
  const [status, setAiStatus] = useState<AiStatus>(AiStatus.DEFAULT)
  const statusRef = useRef(status)
  const [delayContent, setDelayContent] = useState('')
  // 后续会出现复杂的流式数据，业务逻辑不适合放在这里处理，先简单放到delayQueue
  const [delayQueue, setDelayQueue] = useState<any[]>([])
  const [isWriting, setIsWriting] = useState(false) // 打字机是否完成标记

  let queue: any[] = [] // 消息队列
  let timer: NodeJS.Timeout
  const setStatus = (status: AiStatus) => {
    setAiStatus(status)
    statusRef.current = status
  }
  const close = () => {
    abort.current?.abort()
    queue = []
    clearTimeout(timer)
    flushSync(() => {
      setStatus(AiStatus.USER_CLOSE)
      setIsWriting(false)
    })
  }
  const handleMessage = (event: EventSourceMessage, msgCb?: (data: any) => void) => {
    const data = JSON.parse(event.data) as any
    msgCb && msgCb(data)
    // 如果是用户自己处理信息，hook内部不需要处理，减少内部状态的频繁更新
    if (dependOnUser) {
      return
    }
    queue.push(data)
    flushSync(() => {
      statusRef.current === AiStatus.OPEN && setStatus(AiStatus.WRITING)
      setContent((prev) => prev + data.text)
    })
  }

  // 取出第一个单词，并拼接
  const pickText = () => {
    const data = queue.shift()
    if (!data) {
      return
    }

    if (data.text) {
      flushSync(() => {
        setDelayContent((prev) => {
          // useAiWriter里面对数据的处理目前只处理是text类型的数据,过滤掉其他类型，防止干扰
          if (data.type !== 'text') {
            return prev
          }
          const dContent = prev + data.text
          askDelayMsgCb && askDelayMsgCb(dContent)
          return dContent
        })
        setDelayQueue((prev) => {
          return [...prev, data]
        })
      })
    }
  }

  const interval = () => {
    // 建立链接 或者 在接收数据的时候启动  或者不是主动断开，且有长度
    if ([AiStatus.OPEN, AiStatus.WRITING].includes(statusRef.current) || (queue.length && statusRef.current === AiStatus.END)) {
      timer = setTimeout(() => {
        pickText()
        interval()
      }, delayTime)
    }
    if (!queue.length && statusRef.current === AiStatus.END) {
      setIsWriting(false)
    }
  }

  const handleError = (err: any, errorCb?: () => void) => {
    console.log('error', err)
    elkReport({
      name: ELKReportName.streamRequestError,
      message: err,
      grade: 'Error',
      category: 'unknown',
    })
    flushSync(() => setStatus(AiStatus.ERROR))
    errorCb && errorCb()
  }

  const handleClose = (closeCb?: () => void) => {
    // console.log('close')
    if (status !== AiStatus.ERROR) {
      setStatus(AiStatus.END)
      closeCb && closeCb()
    }
  }
  const ask = (data: AiWriterOption, cbs?: CallBack) => {
    const abortController = new AbortController()
    flushSync(() => {
      abort.current = abortController
      setContent('')
      setDelayContent('')
      setDelayQueue([])
      setStatus(AiStatus.DEFAULT)
      setIsWriting(true)
    })

    const mockData = () => {
      let text = `据恒大汽车公告及债转股的条款顿集团股免\n
      恒大汽车公告，纽顿集团战略投资终止，公司此前拟议的交易及债转股的条款修订尚未取得任何进一步进展。恒大汽车表示，将不会召开股东大会以批准，也不会刊发任何内容有关(其中包括)特别授权、纽顿集团股份认购协议、拟议交易、清洗豁免、建议修订、债转股认购协议、抵销协议及债转股的通函。公司将不再就拟议交易及债转股的进展进一步刊发每月公告。由于股份认购不再进行，根据收购守则规则26的规定，认购人无义务就其及与其一致行动人士尚未拥有或同意收购的所有已发行股份提出无条件强制性全面要约。公司将不会向执行人员提出申请，寻求其同意特别交易。
      `

      if (cbs?.openCb) {
        cbs?.openCb()
      }

      handleMessage(
        {
          data: JSON.stringify({
            text: 'Title:',
          }),
        } as EventSourceMessage,
        cbs?.msgCb,
      )
      const output = (index: number) => {
        if (index < text.length) {
          setTimeout(() => {
            const message = {
              data: JSON.stringify({
                text: text[index],
              }),
            }
            handleMessage(message as EventSourceMessage, cbs?.msgCb)
            output(index + 1)
          }, 10)
        } else {
          handleClose()
        }
      }
      setTimeout(() => {
        flushSync(() => {
          setStatus(AiStatus.OPEN)
          if (!dependOnUser) {
            interval()
          }
        })
        setTimeout(() => {
          output(0)
        }, 1000)
      }, 1000)
    }

    // if(isDev) {
    //   mockData()
    //   return
    // }

    fetchEventSource(sseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      openWhenHidden: true, // 控制切换时不关闭sse链接
      body: JSON.stringify(data),
      signal: abort.current?.signal,
      onopen(res) {
        if (res.status !== 200) {
          return Promise.reject()
        }
        flushSync(() => {
          setStatus(AiStatus.OPEN)
          interval()
        })
        // console.log('open')
        if (cbs?.openCb) {
          cbs?.openCb()
        }
        return Promise.resolve()
      },
      onmessage: (event) => handleMessage(event, cbs?.msgCb),
      onerror: (err) => handleError(err, cbs?.errorCb),
      onclose: () => handleClose(cbs?.closeCb),
    })
  }

  return {
    ask,
    close,
    content,
    delayContent,
    delayQueue,
    status,
    abort,
    isWriting,
  }
}
